import { format } from 'date-fns'

import { Performance } from 'generated/types'

import { publishingColors } from '../../styles'

export type FormattedDataType = {
  dates: string[]
  amounts: number[]
}

export const formatChartValues = (
  accountValues: Performance['accountValues'],
): FormattedDataType => {
  if (!accountValues?.length) {
    return {
      dates: [],
      amounts: [],
    }
  }

  const amounts = (accountValues || []).map((accountValue) => {
    return accountValue?.amount ?? 0
  })
  const dates = accountValues.map((accountValue) => {
    const timestamp = accountValue?.timestamp

    return format(new Date(timestamp), 'MMM dd yyyy')
  })

  return {
    dates,
    amounts,
  }
}

export const getOptions = (tension: number) => ({
  animation: false,
  maintainAspectRatio: false,
  layout: {
    padding: {
      bottom: 5,
      top: 5,
    },
  },
  legend: {
    display: false,
  },
  elements: {
    line: {
      tension: tension,
    },
  },
  tooltips: {
    enabled: false,
  },
  scales: {
    xAxes: [
      {
        display: false,
      },
    ],
    yAxes: [
      {
        display: false,
        ticks: {
          max: undefined,
          suggestedMin: undefined,
        },
      },
    ],
  },
})

type DatasetsType = {
  borderColor: string
  borderWidth: number
  data: FormattedDataType['amounts']
  fill: boolean
  label: string
  pointHitRadius: number
  pointHoverRadius: number
  pointRadius: number
}

export const getChartData = ({
  dates,
  amounts,
}: FormattedDataType): { labels: string[]; datasets: DatasetsType[] } => {
  return {
    labels: dates,
    datasets: [
      {
        borderColor: publishingColors.tan,
        borderWidth: 4,
        data: amounts,
        fill: false,
        label: 'chart',
        pointHitRadius: 4,
        pointHoverRadius: 0,
        pointRadius: 0,
      },
    ],
  }
}

// Tool Tips helpers
type getClosestDateArg = {
  elWidth: number
  offsetX: number
  totalDates: number
}

export const getClosestDate = ({
  elWidth,
  offsetX,
  totalDates,
}: getClosestDateArg): { closestIndex: number; tooltipOffsetX: number } => {
  const sectionWidth = elWidth / (totalDates - 1)
  const closestIndex = Math.round(offsetX / sectionWidth)
  return { closestIndex, tooltipOffsetX: closestIndex * sectionWidth }
}

type returnTypes = {
  elWidth: number
  offsetX: number
}

export const getMouseEventValue = (evt: React.MouseEvent): returnTypes => {
  const elWidth = evt.currentTarget.getBoundingClientRect().width
  const offsetX = evt.nativeEvent.offsetX

  return { elWidth, offsetX }
}

export const getTouchEventValue = (evt: React.TouchEvent): returnTypes => {
  const { left, width: elWidth } = evt.currentTarget.getBoundingClientRect()
  const offsetX = Math.round(evt.targetTouches[0].pageX - left)

  return { elWidth, offsetX }
}

type getTooltipOffsetXArg = {
  offsetX: number
  containerWidth: number
  tooltipWidth: number
}

// Get tooltip offsetX while keeping tooltip in bound of container
export const getTooltipOffsetX = ({
  offsetX,
  containerWidth,
  tooltipWidth,
}: getTooltipOffsetXArg): number => {
  const tooltipOffsetX = offsetX - tooltipWidth / 2

  if (tooltipOffsetX < 0) {
    return 0
  } else if (offsetX + tooltipWidth / 2 > containerWidth) {
    return containerWidth - tooltipWidth + 1 // +1 to fill tiny gap that might happen on the right
  }

  return tooltipOffsetX
}
